import { Permissions } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';

import { usePermissionsContext } from 'lib/common/contexts/PermissionsContext';

import { LogEvents, logger } from 'lib/common/components/LoggerController';

import { EXTERNAL_TRANSFER_ATTRIBUTE } from 'lib/common/constants/conference';
import CONNECTION_STATES from 'lib/common/constants/connectionStates';
import quickConnectTypes from 'lib/common/constants/quickConnectTypes';

import getTransferEndpoint from 'lib/common/api/getTransferEndpoint';
import TTask from 'lib/common/types/Task';
import connectAction from 'lib/common/utils/connectAction';
import connectGetter from 'lib/common/utils/connectGetter';
import setContactAttributes from 'lib/common/utils/setContactAttributes';

import useMoveTaskToACW from '../useMoveTaskToACW';
import addTransferConnection from './utils/addTransferConnection';
import getConnectedCallContact from './utils/getConnectedCallContact';
import transferChatTask from './utils/transferChatTask';
import waitForAgentTaskTransfer from './utils/waitForAsyncTaskTransfer';
import waitForContactEnded from './utils/waitForContactEnded';

function getTaskContact(tasks: TTask[], taskId: string) {
  return tasks.find((task) => task.taskId === taskId)?.contact;
}

export default function useTransferActions({ tasks, handleContactChange }) {
  const moveTaskToACW = useMoveTaskToACW({ handleContactChange });
  const { hasPermission } = usePermissionsContext();
  const { config } = useConfigContext();
  const { fetch_, connectUserId } = useAuthContext();

  const onChatTransfer = async (contact: connect.Contact, transferEndpoint: connect.Endpoint) => {
    try {
      await transferChatTask(contact, transferEndpoint);

      await waitForContactEnded(contact);

      return moveTaskToACW(contact);
    } catch {
      // Do nothing/handle gracefully as the transfer failed event will be in the chat
    }
  };

  const onTransferToQueueOrAgent = async (endpoint: connect.Endpoint, taskId: string) => {
    const contact = getTaskContact(tasks, taskId);

    if (!contact) {
      return Promise.resolve();
    }

    const contactType = connectGetter(contact, 'getType');

    if (!contactType) {
      return Promise.resolve();
    }

    if (contactType === connect.ContactType.CHAT) {
      return onChatTransfer(contact, endpoint);
    }

    await addTransferConnection(contact, endpoint);

    if (contactType !== connect.ContactType.TASK) {
      return;
    }

    await waitForAgentTaskTransfer(contact);

    return moveTaskToACW(contact);
  };

  const handleExternalTransfer = async (phoneNumber: string, connectedCallContact: connect.Contact, userId: string) => {
    const endpointARN = await getTransferEndpoint({
      fetch_,
      config,
      type: quickConnectTypes.EXTERNAL_NUMBER,
      identifier: userId,
    });

    if (!endpointARN) {
      logger.error(LogEvents.EXTERNAL_TRANSFER.ENDPOINT.FAIL, { error: 'No endpoint ARN found' });

      return;
    }

    handleContactChange({ contact: connectedCallContact, connectionState: CONNECTION_STATES.HOLD });

    const endpoint = {
      agentLogin: '',
      endpointARN,
      endpointId: endpointARN,
      name: userId,
      phoneNumber: phoneNumber,
      queue: '',
      type: connect.EndpointType.QUEUE,
    };

    await setContactAttributes({
      contactId: connectedCallContact.contactId,
      attributes: [
        {
          key: EXTERNAL_TRANSFER_ATTRIBUTE,
          value: phoneNumber,
        },
      ],
      fetch_,
      agentServiceUrl: config.AGENT_SERVICE_V2_URL,
    });

    logger.info(LogEvents.EXTERNAL_TRANSFER.ATTRIBUTE_SET.SUCCESS);

    if (!connectedCallContact) {
      return Promise.resolve();
    }

    const contactType = connectGetter(connectedCallContact, 'getType');

    if (!contactType) {
      return Promise.resolve();
    }

    await connectAction('addConnection', connectedCallContact, endpoint, {
      ignoreError: () => connectGetter(connectedCallContact, 'getState')?.type === connect.ContactStateType.ENDED,
    });

    if (contactType !== connect.ContactType.TASK && contactType !== connect.ContactType.CHAT) {
      return;
    }

    await waitForAgentTaskTransfer(connectedCallContact);

    await moveTaskToACW(connectedCallContact);

    logger.info(LogEvents.EXTERNAL_TRANSFER.INITIATED.SUCCESS);

    return;
  };

  const onTransferToExternalNumber = async (phoneNumber: string) => {
    const connectedCallContact = getConnectedCallContact(tasks);

    if (!connectedCallContact) {
      return;
    }

    const isExternalTransferFlowEnabled = hasPermission({
      type: 'tenant',
      permission: Permissions.EXTERNAL_TRANSFER_FLOW,
    });

    if (isExternalTransferFlowEnabled && config.EXTERNAL_NUMBER_TRANSFER_CONFIG) {
      return handleExternalTransfer(phoneNumber, connectedCallContact, connectUserId || '');
    }

    return connectAction('addConnection', connectedCallContact, connect.Endpoint.byPhoneNumber(phoneNumber), {
      ignoreError: () => connectGetter(connectedCallContact, 'getState')?.type === connect.ContactStateType.ENDED,
    });
  };

  return {
    onTransferToQueueOrAgent,
    onTransferToExternalNumber,
  };
}
